import { Autocomplete, Checkbox, Grid, Stack, Typography } from '@mui/material';
import {
  CardSection,
  CardSubSection,
  isEmail,
  PhoneNumberField,
  Show,
  StateSelect,
  TextField,
  ZIP_CODE_REGEX,
  ZipField,
} from '@side/pantry';
import type { FocusEvent } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { ClientType } from '@/common/hooks/referrals/types';
import { isValueEqualToCountryPrefix } from '@/common/utils/phoneNumber';
import { StreetAutocomplete } from '@/components/AddressInput/StreetAutocomplete/StreetAutocomplete';
import { FormTextField } from '@/components/Inputs/FormTextField';
import {
  TrackReferralsEvent,
  useTrackReferralsAnalytics,
} from '$analytics/useTrackAnalytics/domains/referrals';
import { getSecondAgent } from '../../utils';
import type { FieldValues } from '../../views/NewReferral/types';
import { ClientFirstNameField } from './ClientFirstNameField';

type ClientDetailsProps = {
  onUpdate?: () => void;
};

const clientTypeOptions = Object.values(ClientType);

export function ClientDetails({ onUpdate }: ClientDetailsProps) {
  const { setValue } = useFormContext<FieldValues>();
  const trackEvent = useTrackReferralsAnalytics();

  const phoneFieldName = 'client.cellPhone';
  const [
    clientTypesList,
    phone,
    state,
    referralType,
    referringAgent,
    transactingAgent,
  ] = useWatch<
    FieldValues,
    [
      'client.clientTypesList',
      typeof phoneFieldName,
      'client.state',
      'referralType',
      'referringAgent',
      'transactingAgent',
    ]
  >({
    name: [
      'client.clientTypesList',
      phoneFieldName,
      'client.state',
      'referralType',
      'referringAgent',
      'transactingAgent',
    ],
  });

  const secondAgent = getSecondAgent({
    referralType,
    referringAgent,
    transactingAgent,
  });
  const isSecondAgentWithSide = secondAgent?.isSideAgent;

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    onUpdate?.();

    trackEvent(TrackReferralsEvent.EditClientField, {
      value: e.target.value,
      eventTarget: e.target.name,
    });
  };

  return (
    <CardSection cardTitle="Primary Client Details">
      <CardSubSection>
        <Grid columnSpacing={2.5} container>
          <Grid size={12}>
            <Controller<FieldValues, 'client.clientTypesList'>
              name="client.clientTypesList"
              rules={{ validate: (value) => value.length > 0 || 'Required' }}
              render={({ field, fieldState: { error, invalid } }) => (
                <Autocomplete
                  autoComplete
                  disableCloseOnSelect
                  disablePortal
                  multiple
                  onChange={(_, newClientTypes, reason, details) => {
                    if (reason === 'clear') {
                      setValue('client.clientTypesList', [], {
                        shouldDirty: true,
                        shouldValidate: true,
                      });

                      trackEvent(TrackReferralsEvent.ClearAllClientTypes);
                    }

                    if (
                      ['removeOption', 'selectOption'].includes(reason) &&
                      Array.isArray(newClientTypes)
                    ) {
                      setValue('client.clientTypesList', newClientTypes, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });

                      trackEvent(TrackReferralsEvent.SelectClientType, {
                        task: reason === 'removeOption' ? 'deselect' : 'select',
                        value: details?.option,
                      });
                    }

                    onUpdate?.();
                  }}
                  options={clientTypeOptions}
                  renderInput={(params) => (
                    <TextField
                      {...field}
                      {...params}
                      highlight={
                        !clientTypesList?.length ? 'secondary' : undefined
                      }
                      label="Client Type"
                      error={invalid}
                      helperText={error?.message}
                      fixedHeight
                      placeholder={
                        clientTypesList?.length
                          ? undefined
                          : 'Select all that apply'
                      }
                      required
                      slotProps={{
                        ...params.slotProps,
                        htmlInput: {
                          ...params.slotProps.htmlInput,
                          readOnly: true,
                        },
                      }}
                    />
                  )}
                  renderOption={(optionProps, option, { selected }) => {
                    return (
                      <Stack
                        {...optionProps}
                        component="li"
                        key={option}
                        sx={{
                          alignItems: 'center',
                          flexDirection: 'row',
                          height: 42,
                          marginY: 0.25,
                          paddingLeft: '0 !important',
                          '&.MuiAutocomplete-option[aria-selected="true"]': {
                            backgroundColor: 'white !important',
                          },
                          '&.MuiAutocomplete-option[aria-selected="true"]:hover':
                            {
                              backgroundColor: (t) =>
                                `${t.palette.action.hover} !important`,
                            },
                        }}
                      >
                        <Checkbox checked={selected} />

                        <Typography sx={{ marginRight: 2 }} variant="body2">
                          {option}
                        </Typography>
                      </Stack>
                    );
                  }}
                  value={clientTypesList}
                  sx={{
                    '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
                      minWidth: 0,
                    },
                  }}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 6, md: 4 }}>
            <ClientFirstNameField onUpdate={onUpdate} />
          </Grid>

          <Grid size={{ xs: 6, md: 4 }}>
            <FormTextField
              name="client.lastName"
              label="Last Name"
              required
              fixedHeight
              onBlur={handleBlur}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <FormTextField
              name="client.nickname"
              label="Nickname"
              fixedHeight
              onBlur={handleBlur}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormTextField
              name="client.email"
              rules={{
                validate: (value: string) =>
                  !value || isEmail(value) || 'Invalid Email',
              }}
              label="Email"
              required
              fixedHeight
              onBlur={handleBlur}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <PhoneNumberField
              fixedHeight
              fullWidth
              id={phoneFieldName}
              label="Phone"
              name={phoneFieldName}
              onChange={(value) => {
                if (isValueEqualToCountryPrefix(value)) {
                  setValue(phoneFieldName, '', {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  return;
                }

                setValue(phoneFieldName, value, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }}
              onBlur={(value) => {
                onUpdate?.();

                trackEvent(TrackReferralsEvent.EditClientField, {
                  value,
                  eventTarget: 'client.phone',
                });
              }}
              value={phone}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 7.5 }}>
            <StreetAutocomplete
              handleAutocompleteSelect={(address) => {
                setValue(`client.city`, address?.city ?? '', {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                setValue(`client.state`, address?.state ?? '', {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                setValue(`client.street1`, address?.street1 ?? '', {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                setValue(`client.zip`, address?.zip ?? '', {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }}
              label="Street"
              name="client.street1"
              required={false}
              onInputBlur={onUpdate}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4.5 }}>
            <FormTextField
              name="client.street2"
              label="Apt/Unit/Suite Number"
              fixedHeight
              onBlur={handleBlur}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormTextField
              name="client.city"
              label="City"
              fixedHeight
              onBlur={handleBlur}
            />
          </Grid>

          <Grid size={{ xs: 6, md: 3 }}>
            <StateSelect
              abbreviations
              AutocompleteProps={{
                onChange: (_, value) => {
                  setValue('client.state', value as string, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  onUpdate?.();
                },
                value: state,
              }}
              TextFieldProps={{
                error: false,
                fixedHeight: true,
                fullWidth: true,
                helperText: undefined,
                label: 'State',
              }}
            />
          </Grid>

          <Grid size={{ xs: 6, md: 3 }}>
            <Controller
              name="client.zip"
              rules={{
                validate: (value: string) =>
                  !value || ZIP_CODE_REGEX.test(value) || 'Invalid Zip Code',
              }}
              render={({ field }) => (
                <ZipField {...field} onBlur={handleBlur} label="Zip Code" />
              )}
            />
          </Grid>
        </Grid>
      </CardSubSection>

      <Show when={isSecondAgentWithSide}>
        <CardSubSection>
          <FormTextField
            name="notes"
            label="Notes"
            slotProps={{
              input: {
                minRows: 5,
              },
            }}
            multiline
            variant="outlined"
            onBlur={handleBlur}
          />
        </CardSubSection>
      </Show>
    </CardSection>
  );
}
